import { FC, useState, memo, useRef, useEffect, useCallback, useMemo } from 'react';
import { colors } from '../utils/colorDict';
import IconButton from "@mui/material/IconButton";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import { createPortal } from 'react-dom';

const SCROLL_AMOUNT = 600;
const PAN_DEBOUNCE_MS = 800;
const BAR_HEIGHT = 33;
const THUMB_HEIGHT = 120;
const OPEN_HEIGHT = BAR_HEIGHT + THUMB_HEIGHT + 8;
const MAX_CONCURRENT_LOADS = 3; // IIIF server can't handle more than ~5 concurrent; fetchQueue uses 3 more

// Global loading semaphore — limits concurrent <img> loads to avoid 502s from IIIF server
const loadQueue: Array<() => void> = [];
let activeLoads = 0;

function acquireLoadSlot(): Promise<void> {
    if (activeLoads < MAX_CONCURRENT_LOADS) {
        activeLoads++;
        return Promise.resolve();
    }
    return new Promise(resolve => { loadQueue.push(resolve); });
}

function releaseLoadSlot() {
    activeLoads--;
    if (loadQueue.length > 0) {
        activeLoads++;
        const next = loadQueue.shift()!;
        next();
    }
}

// Matches the highlighted map dot — vivid magenta-purple from the hue-rotated arrow
const MARKED_FRAME_COLOR = '#d126e7';

// Single thumbnail — IntersectionObserver detects visibility, semaphore limits concurrency
const Thumb: FC<any> = memo(({ item, handleModalData, handleMarkedThumbnail, isMarked }) => {
    const [visible, setVisible] = useState(false);
    const [canLoad, setCanLoad] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [failed, setFailed] = useState(false);
    // If the primary URL errors and a fallback exists (Scholten: Leiden → NAS),
    // switch to it before falling back to the 3s retry loop.
    const [usingFallback, setUsingFallback] = useState(false);
    const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null);
    const ref = useRef<HTMLDivElement>(null);
    const slotAcquired = useRef(false);

    // IntersectionObserver: detect when thumbnail nears viewport
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
            { rootMargin: '0px 400px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const slotReleased = useRef(false);

    // Acquire a load slot when visible; release on unmount if not yet done
    useEffect(() => {
        if (!visible || slotAcquired.current) return;
        slotAcquired.current = true;
        acquireLoadSlot().then(() => setCanLoad(true));
        return () => {
            // Release slot if component unmounts before image finishes
            if (slotAcquired.current && !slotReleased.current) {
                slotReleased.current = true;
                releaseLoadSlot();
            }
        };
    }, [visible]);

    const handleLoad = () => {
        setLoaded(true);
        if (!slotReleased.current) { slotReleased.current = true; releaseLoadSlot(); }
    };

    const fallbackUrl = item.properties.thumbnail_fallback as string | undefined;
    const handleError = () => {
        // First error on the primary URL: if a fallback exists, swap to it immediately.
        if (!usingFallback && fallbackUrl) {
            setUsingFallback(true);
            return;
        }
        if (retryCount < 2) {
            setTimeout(() => setRetryCount(c => c + 1), 3000);
        } else {
            setFailed(true);
            if (!slotReleased.current) { slotReleased.current = true; releaseLoadSlot(); }
        }
    };

    const baseUrl = canLoad ? ((usingFallback && fallbackUrl) || item.properties.thumbnail_url) : null;
    const imgUrl = baseUrl ? (retryCount > 0 ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}_r=${retryCount}` : baseUrl) : null;

    return (
        <>
            <div
                ref={ref}
                onMouseEnter={() => {
                    if (!ref.current) return;
                    const rect = ref.current.getBoundingClientRect();
                    setHoverPos({ x: rect.left + rect.width / 2, y: rect.top });
                }}
                onMouseLeave={() => setHoverPos(null)}
                onClick={(e) => {
                    e.stopPropagation();
                    setHoverPos(null);
                    if (e.detail === 1) handleMarkedThumbnail(item);
                    else if (e.detail === 2) handleModalData(item);
                }}
                style={{
                    flexShrink: 0,
                    height: THUMB_HEIGHT,
                    minWidth: '80px',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: loaded ? '#333' : '#e0e0e0',
                    cursor: 'pointer',
                    margin: '0 2px',
                }}
            >
                {failed && <BrokenImageIcon sx={{ position: 'absolute', color: '#bbb', fontSize: 32 }} />}
                {imgUrl && !failed && (
                    <img
                        src={imgUrl}
                        alt=""
                        onLoad={handleLoad}
                        onError={handleError}
                        style={{
                            height: THUMB_HEIGHT,
                            width: 'auto',
                            objectFit: 'contain',
                            opacity: loaded ? 1 : 0,
                            transition: 'opacity 0.2s',
                        }}
                    />
                )}
                {isMarked && (
                    /* Frame drawn entirely inside the thumb box (inset shadow) so
                       the thumb's outer footprint doesn't change when it gets
                       marked — same overall size, magenta painted inwards. */
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        boxShadow: `inset 0 0 0 3px ${MARKED_FRAME_COLOR}`,
                        pointerEvents: 'none',
                    }} />
                )}
            </div>
            {hoverPos && loaded && createPortal(
                <div style={{
                    position: 'fixed',
                    left: hoverPos.x,
                    bottom: `calc(100vh - ${hoverPos.y}px + 8px)`,
                    transform: 'translateX(-50%)',
                    zIndex: 9999,
                    pointerEvents: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    backgroundColor: '#222',
                }}>
                    <img
                        src={item.properties.modal_url}
                        alt=""
                        style={{ display: 'block', height: '240px', width: 'auto', objectFit: 'contain' }}
                    />
                </div>,
                document.body
            )}
        </>
    );
});

const PanelThumbLayer: FC<any> = memo(({
    handleMarkedThumbnail,
    markedThumbnail,
    thumbDrawerOpen,
    handleThumbDrawerOpen,
    mapRef,
    selectedMedia,
    handleModalData,
    openModalWithList,
    modalOpen,
    items,
    mapCenter: mapCenterProp,
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<any>(null);

    const [debouncedCenter, setDebouncedCenter] = useState<[number, number] | null>(mapCenterProp || null);

    useEffect(() => {
        if (!mapCenterProp) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedCenter(mapCenterProp);
        }, PAN_DEBOUNCE_MS);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [mapCenterProp]);

    const sortedItems = useMemo(() => {
        if (!debouncedCenter) return items;
        const [cLng, cLat] = debouncedCenter;
        return [...items].sort((a, b) => {
            const aCoords = a.geometry?.coordinates;
            const bCoords = b.geometry?.coordinates;
            if (!aCoords) return 1;
            if (!bCoords) return -1;
            const distA = (aCoords[0] - cLng) ** 2 + (aCoords[1] - cLat) ** 2;
            const distB = (bCoords[0] - cLng) ** 2 + (bCoords[1] - cLat) ** 2;
            return distA - distB;
        });
    }, [items, debouncedCenter]);

    const itemIdsKey = useMemo(() => items.map((i: any) => i.id).sort().join(','), [items]);
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollLeft = 0;
    }, [itemIdsKey]);

    const scrollBy = useCallback((dir: number) => {
        scrollRef.current?.scrollBy({ left: dir * SCROLL_AMOUNT, behavior: 'smooth' });
    }, []);

    // Arrow-key navigation: only when slider is open, a thumb is marked, and modal is closed
    useEffect(() => {
        if (!thumbDrawerOpen || modalOpen || !markedThumbnail) return;
        const handleArrow = (e: KeyboardEvent) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
            if (sortedItems.length === 0) return;
            const markedId = markedThumbnail.id || markedThumbnail.properties?.id;
            const currentIdx = sortedItems.findIndex((i: any) => (i.id || i.properties?.id) === markedId);
            if (currentIdx < 0) return;
            e.preventDefault();
            const dir = e.key === 'ArrowRight' ? 1 : -1;
            const nextIdx = (currentIdx + dir + sortedItems.length) % sortedItems.length;
            const nextItem = sortedItems[nextIdx];
            handleMarkedThumbnail(nextItem);
            // Scroll the thumb into view
            const el = scrollRef.current?.children[nextIdx] as HTMLElement | undefined;
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        };
        document.addEventListener('keydown', handleArrow);
        return () => document.removeEventListener('keydown', handleArrow);
    }, [thumbDrawerOpen, modalOpen, markedThumbnail, sortedItems, handleMarkedThumbnail]);

    return (
        <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 2,
            height: thumbDrawerOpen ? OPEN_HEIGHT : BAR_HEIGHT,
            transition: 'height 0.3s ease',
            overflow: 'hidden',
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            backdropFilter: 'blur(4px)',
            boxShadow: '0 -1px 6px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
        }}>
            {/* Toggle bar — always at top, visible even when collapsed */}
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: BAR_HEIGHT,
                flexShrink: 0,
            }}>
                <IconButton
                    onClick={handleThumbDrawerOpen}
                    sx={{ color: 'gray', padding: '1px' }}
                    size="small"
                >
                    {thumbDrawerOpen
                        ? <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                        : <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                    }
                </IconButton>
            </div>

            {/* Thumbnail strip — below toggle, hidden by overflow when closed */}
            <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                height: THUMB_HEIGHT + 8,
                padding: '0 0 8px 0',
            }}>
                <IconButton
                    onClick={() => scrollBy(-1)}
                    sx={{
                        position: 'absolute', left: 4, zIndex: 3,
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.95)' },
                        width: 28, height: 28,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}
                >
                    <ArrowLeftIcon />
                </IconButton>

                <div
                    ref={scrollRef}
                    style={{
                        display: 'flex',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        height: THUMB_HEIGHT,
                        width: '100%',
                        padding: '0 36px',
                        scrollbarWidth: 'none',
                    }}
                >
                    {thumbDrawerOpen && sortedItems.map((item) => {
                        const markedId = markedThumbnail?.id ?? markedThumbnail?.properties?.id;
                        const itemId = item.id ?? item.properties?.id;
                        return (
                            <Thumb
                                key={item.id}
                                item={item}
                                isMarked={markedId != null && itemId === markedId}
                                handleModalData={(clicked: any) => openModalWithList ? openModalWithList(clicked, sortedItems) : handleModalData(clicked)}
                                handleMarkedThumbnail={handleMarkedThumbnail}
                            />
                        );
                    })}
                </div>

                <IconButton
                    onClick={() => scrollBy(1)}
                    sx={{
                        position: 'absolute', right: 4, zIndex: 3,
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.95)' },
                        width: 28, height: 28,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}
                >
                    <ArrowRightIcon />
                </IconButton>
            </div>

        </div>
    );
});

export default PanelThumbLayer;
